/**
 * SOH bucket migration (history sankey) helpers. Pure functions: no React, no API.
 *
 *   buildSohMigrationChartInput  history/list chart response -> BucketSankeyChart input
 *   filterSohHistoryDrillDownSchema  org meta drill-down schema -> fields the history
 *                                    list endpoint supports
 */
import { getNextMonthKey, formatMonthLabel, getMonthDateRange } from "../../../../Helper/DatePicker/MonthRange";
import { toCount } from "../../../../Charts/Sankey/sankeyGraph";
import { SOH_UNAVAILABLE_LABEL, getBucketDisplayLabel } from "./unavailableBucket";
import { UNKNOWN_BUCKET_COLOR } from "../../../../Charts/Sankey/sankeyTokens";
import { getSohHistoryFieldSets } from "./sohHistoryFields";
import { shouldWaitForGlobalFilter } from "../../../../Helper/Inventory/inventoryLoadHelper";

/**
 * Largest window the history endpoint accepts, in calendar months (inclusive).
 * Mirrors SOH_HISTORY_MAX_MONTHS in evcell-v1.1 app/services/soh_history_service.py;
 * a longer window is rejected with 400, so keep the two in step.
 */
export const SOH_HISTORY_MAX_MONTHS = 12;

/** Months shown when org meta does not set a valid count. */
export const SOH_MIGRATION_DEFAULT_MONTHS = 12;

/**
 * Number of months the chart requests (the last N complete months, ending with the
 * previous month), from org meta charts.sohMigrationSankeyChart.months:
 *   - a finite number is rounded DOWN to a whole month (7.9 -> 7);
 *   - missing, not a number (strings included), or under 1 after rounding -> 12;
 *   - more than SOH_HISTORY_MAX_MONTHS -> SOH_HISTORY_MAX_MONTHS.
 * Leading months with no SOH are trimmed afterwards (buildSohMigrationChartInput).
 */
export function resolveSohMigrationMonths(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return SOH_MIGRATION_DEFAULT_MONTHS;
  const months = Math.floor(value);
  if (months < 1) return SOH_MIGRATION_DEFAULT_MONTHS;
  return Math.min(months, SOH_HISTORY_MAX_MONTHS);
}

/**
 * True when a month has SOH for at least one battery: some bucket other than
 * "SOH Unavailable" has a count. The API returns every month in the window with all
 * in-scope batteries, so a month before SOH was imported holds only "SOH Unavailable".
 */
export function monthHasSoh(month) {
  return (Array.isArray(month?.metricsArray) ? month.metricsArray : []).some(
    (entry) => entry?.type != null && entry.type !== "" && entry.type !== SOH_UNAVAILABLE_LABEL && toCount(entry.value) > 0
  );
}

/** Colour for a bucket label the API returns that is not in the org meta list. */
export const UNKNOWN_SOH_BUCKET_COLOR = UNKNOWN_BUCKET_COLOR;

/**
 * Legend / tooltip label for a bucket: "SOH <label>", unless the label already
 * starts with "SOH" (e.g. "SOH Unavailable").
 */
export function toSohBucketDisplayLabel(label) {
  const text = String(label ?? "").trim();
  if (!text) return "SOH";
  return /^soh\b/i.test(text) ? text.replace(/^soh\b/i, "SOH") : `SOH ${text}`;
}

const isUnavailableBucket = (b) => b?.isUnavailable === true || b?.name === SOH_UNAVAILABLE_LABEL;

/**
 * Maps the history/list chart response to BucketSankeyChart's input.
 *
 * Bucket order follows `sohBuckets` (useSOHBuckets: best -> worst, "SOH Unavailable"
 * last). A label from the API that is not in that list is appended after the known
 * range buckets and before "SOH Unavailable", in first-seen order, so it never
 * crashes the chart.
 *
 * LEADING months with no SOH (monthHasSoh false) are dropped, with their ribbons, so
 * the chart starts at the first month that has data; an empty month in the middle is
 * kept. If no month has SOH, the result is empty (hasData false).
 *
 * @param {Array} apiData   response.data: [{ xLabel, metricsArray: [{ type, value, nextMonth }] }]
 * @param {Array} sohBuckets  [{ name, displayLabel, color, isUnavailable? }]
 * @returns {{ columns, buckets, nodes, flows, hasData: boolean }}
 */
export function buildSohMigrationChartInput(apiData, sohBuckets) {
  const sorted = (Array.isArray(apiData) ? apiData : [])
    .filter((m) => m && m.xLabel != null && m.xLabel !== "")
    .slice()
    .sort((a, b) => String(a.xLabel).localeCompare(String(b.xLabel)));
  const firstWithSoh = sorted.findIndex(monthHasSoh);
  const months = firstWithSoh < 0 ? [] : sorted.slice(firstWithSoh);

  const known = (Array.isArray(sohBuckets) ? sohBuckets : []).filter((b) => b?.name != null);
  const knownNames = new Set(known.map((b) => b.name));
  const unknownNames = [];
  const noteBucket = (type) => {
    if (type == null || type === "") return false;
    if (!knownNames.has(type) && !unknownNames.includes(type)) unknownNames.push(type);
    return true;
  };

  const columns = months.map((m) => ({ key: String(m.xLabel), label: formatMonthLabel(m.xLabel) }));
  const nodes = [];
  const flows = [];

  months.forEach((month) => {
    const columnKey = String(month.xLabel);
    const nextKey = getNextMonthKey(columnKey);
    (Array.isArray(month.metricsArray) ? month.metricsArray : []).forEach((entry) => {
      if (!noteBucket(entry?.type)) return;
      const count = toCount(entry.value);
      if (count > 0) nodes.push({ columnKey, bucketId: entry.type, count });

      (Array.isArray(entry.nextMonth) ? entry.nextMonth : []).forEach((next) => {
        if (!noteBucket(next?.type) || !nextKey) return;
        const flowCount = toCount(next.value);
        if (flowCount <= 0) return;
        flows.push({
          fromColumnKey: columnKey,
          toColumnKey: nextKey,
          fromBucketId: entry.type,
          toBucketId: next.type,
          count: flowCount,
        });
      });
    });
  });

  const toChartBucket = (b) => ({
    id: b.name,
    label: toSohBucketDisplayLabel(b.displayLabel ?? b.name),
    color: b.color || UNKNOWN_SOH_BUCKET_COLOR,
  });
  const unknownBuckets = unknownNames.map((name) => ({
    id: name,
    label: toSohBucketDisplayLabel(name),
    color: UNKNOWN_SOH_BUCKET_COLOR,
  }));
  const buckets = [
    ...known.filter((b) => !isUnavailableBucket(b)).map(toChartBucket),
    ...unknownBuckets,
    ...known.filter(isUnavailableBucket).map(toChartBucket),
  ];

  return { columns, buckets, nodes, flows, hasData: nodes.length > 0 };
}

/**
 * The drill-down modal's selectedDataPoint for a clicked sankey node (one bucket in
 * one month). sohCategory is the wire label sent as the StateOfHealthBucket filter;
 * startDate / endDate are the clicked month's first and last day.
 * @returns {object|null}  null when the click has no bucket or an unreadable month
 */
export function buildMigrationDrillDownPoint(click, sohBuckets) {
  const bucket = click?.bucketId;
  const range = getMonthDateRange(click?.columnKey);
  if (bucket == null || bucket === "" || !range) return null;
  return {
    sohCategory: bucket,
    sohCategoryLabel: getBucketDisplayLabel(bucket, sohBuckets),
    locationLabel: "All",
    monthKey: String(click.columnKey),
    monthLabel: formatMonthLabel(click.columnKey),
    startDate: range.startDate,
    endDate: range.endDate,
  };
}

/** Filter name the history list accepts for "batteries that moved from X to Y". */
export const SOH_BUCKET_TRANSITION_FILTER = "StateOfHealthBucketTransition";

/**
 * History list filter for one ribbon: the batteries in bucket `from` at month M and in
 * bucket `to` at month M+1. Both are wire labels (e.g. "95-100", "SOH Unavailable").
 * @returns {{ name: string, value: {from: string, to: string}[] } | null}
 */
export function buildBucketTransitionFilter(from, to) {
  const ok = (v) => typeof v === "string" && v !== "";
  if (!ok(from) || !ok(to)) return null;
  return { name: SOH_BUCKET_TRANSITION_FILTER, value: [{ from, to }] };
}

/**
 * The drill-down modal's selectedDataPoint for a clicked ribbon (from bucket at month M
 * to bucket at month M+1). No single-bucket filter (sohCategory is empty): the
 * transition filter goes in `transitionFilter`, and the window is the first day of M
 * to the last day of M+1 (the list's SOH fields are month M+1 values).
 *
 * @param {{fromColumnKey:string, toColumnKey:string, fromBucketId:string, toBucketId:string}} click
 * @param {Array} sohBuckets
 * @returns {object|null}  null for a missing bucket or an unreadable month
 */
export function buildMigrationTransitionDrillDownPoint(click, sohBuckets) {
  const transitionFilter = buildBucketTransitionFilter(click?.fromBucketId, click?.toBucketId);
  const fromRange = getMonthDateRange(click?.fromColumnKey);
  const toRange = getMonthDateRange(click?.toColumnKey);
  if (!transitionFilter || !fromRange || !toRange) return null;
  const fromLabel = getBucketDisplayLabel(click.fromBucketId, sohBuckets);
  const toLabel = getBucketDisplayLabel(click.toBucketId, sohBuckets);
  const fromMonth = formatMonthLabel(click.fromColumnKey);
  const toMonth = formatMonthLabel(click.toColumnKey);
  return {
    kind: "transition",
    sohCategory: "",
    sohCategoryLabel: `${fromLabel} \u2192 ${toLabel}`,
    locationLabel: "All",
    fromBucket: click.fromBucketId,
    toBucket: click.toBucketId,
    fromMonthKey: String(click.fromColumnKey),
    toMonthKey: String(click.toColumnKey),
    monthLabel: `${fromMonth} \u2192 ${toMonth}`,
    subtitle: `${fromMonth} \u2192 ${toMonth}`,
    startDate: fromRange.startDate,
    endDate: toRange.endDate,
    transitionFilter,
  };
}

/**
 * Extra history-list filters for a drill-down data point: the ribbon's transition
 * filter after any filters the modal already passes. Node points add nothing.
 */
export function withTransitionFilter(additionalFilters, dataPoint) {
  const base = Array.isArray(additionalFilters) ? additionalFilters : [];
  return dataPoint?.transitionFilter ? [...base, dataPoint.transitionFilter] : base;
}

/**
 * History drill-down options from a clicked data point: its window, the unit level
 * selected when it was clicked, and the bucket list the chart was drawn with
 * (requestBuckets), so the bucket labels in the filters resolve.
 */
export const buildHistoryDrillDownOptions = (dataPoint) => ({
  startDate: dataPoint?.startDate,
  endDate: dataPoint?.endDate,
  entityType: dataPoint?.entityType,
  sohBuckets: dataPoint?.requestBuckets,
});

/**
 * Whether the chart must hold its fetch until the operational-status filter has
 * settled. With that filter on, the page fills globalFilters.operationalStatus once its
 * options load (or [] if they fail); fetching earlier sends the 12-month query once
 * without the header and again with it. Same rule as Inventory's
 * shouldWaitForGlobalFilter: wait only when the filter applies and is not ready.
 */
export const shouldWaitForSohMigrationFilters = (waitForOperationalStatus, globalFilters) =>
  shouldWaitForGlobalFilter(waitForOperationalStatus, Array.isArray(globalFilters?.operationalStatus));

// ─── History drill-down schema ────────────────────────────────────────────────

/** True for a field the history list returns as a column value at `level` (select or metaInfo). */
export function isSohHistoryColumnField(field, level = "battery") {
  const { group, select } = getSohHistoryFieldSets(level);
  return typeof field === "string" && (select.has(field) || group.has(field));
}

/** Renderers of the vehicle (Bus Number) cell: LicensePlateCell, which reads pingStatus. */
export const LICENSE_PLATE_RENDERERS = Object.freeze(["vehicle-license-plate", "digital-twin-link"]);

/** True when a kept column renders the vehicle cell (and so needs its status fields). */
export function hasLicensePlateColumn(columnsConfig) {
  return Object.values(columnsConfig || {}).some((c) => LICENSE_PLATE_RENDERERS.includes(c?.valueRenderingType));
}

/** Default unit column added at pack level when the table has none. */
export const DEFAULT_PACK_ID_COLUMN = Object.freeze({
  title: "Pack ID",
  headerLabel: "Pack ID",
  dataIndex: "packID",
  key: "packID",
  valueRenderingType: "text",
  width: 180,
  sorter: false,
});

/**
 * Default unit column added at string level when the table has none: stringDisplayID,
 * falling back to stringNo (the "soh-string-label" renderer).
 */
export const DEFAULT_STRING_COLUMN = Object.freeze({
  title: "String",
  headerLabel: "String",
  dataIndex: "stringDisplayID",
  key: "stringDisplayID",
  valueRenderingType: "soh-string-label",
  width: 140,
  sorter: false,
});

const hasColumnFor = (columnsConfig, fields) =>
  Object.values(columnsConfig).some((c) => fields.includes(c?.dataIndex));

/**
 * Narrows a drill-down table schema to the history list endpoint's allowlists for one
 * level (getSohHistoryFieldSets, which mirrors the backend):
 *   select     kept when select-allowed at the level; a field the endpoint only accepts
 *              as metaInfo (ratedEnergy, presentCapacity, presentEnergy, ...) moves to
 *              metaInfo so its column still gets a value; anything else is dropped
 *   metaInfo   kept when group-allowed at the level
 *   columns    kept when the column's dataIndex is allowed at the level; a kept column
 *              whose sort key (config.key, else its map key -- the key the table sends as
 *              sortBy) is not sortable at the level gets sorter: false
 * So a table config shared between levels never causes a 400: packID is dropped at
 * battery / string, stringNo / stringDisplayID at battery / pack, and the rated /
 * present capacity and energy fields at pack / string.
 *
 * Fields added even if org meta does not list them:
 *   - vehicle cell (LICENSE_PLATE_RENDERERS) present: pingStatus (status dot), and
 *     operationalStatus when options.includeOperationalStatus is set;
 *   - batteryID, always: the drill-down groups by entityID, so the row id (xLabel) is the
 *     unit's id, not the battery's; flattenRow takes batteryID from xLabel unless metaInfo
 *     carries it, and the Battery ID column and the Digital Twin link read batteryID;
 *   - pack level without a packID column: DEFAULT_PACK_ID_COLUMN after the first column
 *     (+ packID); string level without a string column: DEFAULT_STRING_COLUMN after the
 *     first column (+ stringDisplayID, stringNo).
 * Order is preserved; the input is not mutated. No result cache: the modal's
 * resolveDrillDownSchema builds a new schema object on every call, so a cache keyed on
 * the object never hit; the modal's keepIfSameItems keeps select / metaInfo stable.
 *
 * @param {{ columnsConfig?: object, columnOrdering?: string[], select?: string[], metaInfo?: string[] }} schema
 * @param {{ includeOperationalStatus?: boolean, level?: "battery"|"pack"|"string" }} [options]
 * @returns {{ columnsConfig: object, columnOrdering: string[], select: string[], metaInfo: string[] }}
 */
export function filterSohHistoryDrillDownSchema(schema = {}, { includeOperationalStatus = false, level = "battery" } = {}) {
  const { group, select: selectable, sort } = getSohHistoryFieldSets(level);
  const select = Array.isArray(schema?.select) ? schema.select : [];
  const metaInfo = Array.isArray(schema?.metaInfo) ? schema.metaInfo : [];
  const columnsConfig = schema?.columnsConfig && typeof schema.columnsConfig === "object" ? schema.columnsConfig : {};
  const columnOrdering = Array.isArray(schema?.columnOrdering) ? schema.columnOrdering : Object.keys(columnsConfig);

  const nextSelect = [];
  const nextMetaInfo = [];
  const addOnce = (list, field) => { if (!list.includes(field)) list.push(field); };
  metaInfo.forEach((f) => { if (group.has(f)) addOnce(nextMetaInfo, f); });
  select.forEach((f) => {
    if (selectable.has(f)) addOnce(nextSelect, f);
    else if (group.has(f)) addOnce(nextMetaInfo, f);
  });

  const nextColumnsConfig = {};
  Object.keys(columnsConfig).forEach((key) => {
    const config = columnsConfig[key];
    const field = config?.dataIndex;
    if (typeof field !== "string" || !(selectable.has(field) || group.has(field))) return;
    const sortKey = config.key || key;
    nextColumnsConfig[key] = config.sorter && !sort.has(sortKey) ? { ...config, sorter: false } : config;
  });
  const nextColumnOrdering = columnOrdering.filter((key) => key in nextColumnsConfig);

  // Unit column for pack / string, right after the first (Serial / Bus Number) column.
  const insertAfterFirst = (key, config) => {
    nextColumnsConfig[key] = config;
    nextColumnOrdering.splice(Math.min(1, nextColumnOrdering.length), 0, key);
  };
  if (level === "pack" && !hasColumnFor(nextColumnsConfig, ["packID"])) {
    insertAfterFirst(DEFAULT_PACK_ID_COLUMN.key, { ...DEFAULT_PACK_ID_COLUMN });
  }
  if (level === "string" && !hasColumnFor(nextColumnsConfig, ["stringDisplayID", "stringNo"])) {
    insertAfterFirst(DEFAULT_STRING_COLUMN.key, { ...DEFAULT_STRING_COLUMN });
  }
  if (level === "pack") addOnce(nextMetaInfo, "packID");
  if (level === "string") {
    addOnce(nextMetaInfo, "stringDisplayID");
    addOnce(nextMetaInfo, "stringNo");
  }
  // The row id is the unit id at every level (groupBy entityID), so batteryID must come
  // back in metaInfo for the Battery ID column and the Digital Twin link.
  addOnce(nextMetaInfo, "batteryID");

  if (hasLicensePlateColumn(nextColumnsConfig)) {
    addOnce(nextMetaInfo, "pingStatus");
    if (includeOperationalStatus) addOnce(nextMetaInfo, "operationalStatus");
  }

  return {
    columnsConfig: nextColumnsConfig,
    columnOrdering: nextColumnOrdering,
    select: nextSelect,
    metaInfo: nextMetaInfo,
  };
}

/** Stable schemaFilter props for the modal (battery level; one per indicator state). */
export const filterSohHistorySchema = (schema) => filterSohHistoryDrillDownSchema(schema);
export const filterSohHistorySchemaWithOperationalStatus = (schema) =>
  filterSohHistoryDrillDownSchema(schema, { includeOperationalStatus: true });

/** Stable schemaFilter for a level and indicator state (same function per combination). */
const levelFilters = new Map();
export function getSohHistorySchemaFilter(level = "battery", includeOperationalStatus = false) {
  const key = `${level}|${includeOperationalStatus}`;
  if (!levelFilters.has(key)) {
    levelFilters.set(key, (schema) => filterSohHistoryDrillDownSchema(schema, { includeOperationalStatus, level }));
  }
  return levelFilters.get(key);
}
